import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import CartPurchaseHandler from "./CartPurchaseHandler";
import MultiLevelCommissionService from "./MultiLevelCommissionService";
import AutoFillProgramService from "./autoFill/AutoFillProgramService";
import { applyCashRoyaltyTransferFromOrder } from "./pointsToRoyaltyTransferService";
import {
  CURRENT_ACTIVATION_FEE,
  isCurrentTierActivationFee,
} from "../config/membershipConfig";
import {
  orderQualifiesForActivationRoyalty,
  resolveActivationFeeForRoyalty,
} from "../config/activationRoyaltyConfig";
import { isAutoFillUpgradeOrder } from "../utils/autoFillMembershipEnrollment";
import { resolveActivationFeeFromPackageOrder } from "../utils/membershipUpgrade";

async function loadOrderRecord({ firestoreDocId, cartOrderId }) {
  if (firestoreDocId) {
    const snap = await getDoc(doc(db, "cartOrders", firestoreDocId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }
  if (cartOrderId) {
    const snap = await getDocs(
      query(collection(db, "cartOrders"), where("cartOrderId", "==", cartOrderId))
    );
    if (snap.empty) return null;
    const docs = snap.docs.sort((a, b) => {
      const ta = new Date(a.data().createdAt || 0).getTime();
      const tb = new Date(b.data().createdAt || 0).getTime();
      return tb - ta;
    });
    const chosen = docs[0];
    return { id: chosen.id, ...chosen.data() };
  }
  return null;
}

/**
 * Full post-payment order confirmation (commissions, membership, AFP, stock, etc.).
 */
export async function confirmCartOrder(order, options = {}) {
  const confirmedBy = options.confirmedBy || "payment_gateway";

  if (!order?.id) {
    return { success: false, error: "Order document id is required" };
  }
  if (order.orderStatus === "confirmed") {
    return { success: true, alreadyConfirmed: true, message: "Order already confirmed" };
  }

  const orderRef = doc(db, "cartOrders", order.id);

  let stockDeductionResults = [];
  if (order.items && order.items.length > 0 && !order.stockDeducted) {
    for (const orderItem of order.items) {
      try {
        const itemId = orderItem.id || orderItem.itemId || orderItem.productId;
        if (!itemId) continue;

        const itemRef = doc(db, "items", itemId);
        const itemSnap = await getDoc(itemRef);
        if (!itemSnap.exists()) continue;

        const currentStock = itemSnap.data().stockQuantity || 0;
        const orderedQty = orderItem.quantity || 1;
        const newStock = Math.max(0, currentStock - orderedQty);

        await updateDoc(itemRef, {
          stockQuantity: newStock,
          updatedAt: new Date().toISOString(),
        });
        stockDeductionResults.push({
          item: orderItem.name,
          status: "success",
          before: currentStock,
          after: newStock,
          deducted: orderedQty,
        });
      } catch (stockError) {
        console.error("Stock deduction error:", stockError);
      }
    }
  }

  await updateDoc(orderRef, {
    orderStatus: "confirmed",
    paymentStatus: "confirmed",
    confirmedAt: new Date().toISOString(),
    confirmedBy,
    updatedAt: new Date().toISOString(),
    stockDeducted: true,
    stockDeductionResults,
  });

  try {
    const cartRef = doc(db, "userCarts", order.buyerId);
    await deleteDoc(cartRef);
  } catch (cartError) {
    console.error("Error clearing cart:", cartError);
  }

  if ((order.packages && order.packages.length > 0) || order.isPackagePurchase) {
    const userSnapshot = await getDocs(
      query(collection(db, "users"), where("uid", "==", order.buyerId))
    );

    if (!userSnapshot.empty) {
      const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
      const userData = userSnapshot.docs[0].data();
      const isFirstPackagePurchase = !userData.hasPurchasedPackage;
      const hasNoCashback = (userData.cashbackEarned || 0) === 0;
      const packageData = order.packages?.[0];
      const isAutoFillUpgradeOrder =
        order.isAutoFillUpgrade === true ||
        packageData?.isAutoFillUpgrade === true ||
        packageData?.category === "Auto Fill Upgrade";

      if (isAutoFillUpgradeOrder) {
        await updateDoc(userDocRef, {
          autoFillProgramPurchased: true,
          autoFillProgramPurchasedAt: new Date().toISOString(),
          purchasedActivationFee: CURRENT_ACTIVATION_FEE,
          lastAutoFillUpgradeOrderId: order.cartOrderId,
          updatedAt: new Date().toISOString(),
        });
        try {
          await CartPurchaseHandler.addAutoFillUpgradeCashback(order.buyerId);
        } catch (cashbackError) {
          console.error("Auto-fill upgrade cashback:", cashbackError);
        }
      } else if (packageData) {
        const activationFeeFromOrder = resolveActivationFeeFromPackageOrder(
          packageData,
          order
        );

        const purchasedPackageData = {
          id: packageData.id,
          name: packageData.name,
          price: packageData.price,
          description: packageData.description,
          category: packageData.category || "Package",
          activationFee: activationFeeFromOrder,
          purchasedAt: new Date().toISOString(),
          orderId: order.cartOrderId,
          confirmed: true,
          confirmedAt: new Date().toISOString(),
        };

        const membershipUpdate = {
          hasPurchasedPackage: true,
          purchasedPackage: purchasedPackageData,
          purchasedActivationFee: activationFeeFromOrder,
          packagePurchaseDate: new Date().toISOString(),
          lastPackagePurchase: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        if (isCurrentTierActivationFee(activationFeeFromOrder)) {
          membershipUpdate.autoFillProgramPurchased = true;
          membershipUpdate.autoFillProgramPurchasedAt = new Date().toISOString();
        }
        await updateDoc(userDocRef, membershipUpdate);

        if (isFirstPackagePurchase && hasNoCashback) {
          try {
            await CartPurchaseHandler.addFirstTimeCashback(
              order.buyerId,
              activationFeeFromOrder
            );
          } catch (cashbackError) {
            console.error("First-time cashback:", cashbackError);
          }
        }
        if (isCurrentTierActivationFee(activationFeeFromOrder)) {
          try {
            await CartPurchaseHandler.topUpMembershipCashbackTo649Tier(
              order.buyerId
            );
          } catch (topUpErr) {
            console.error("Membership cashback top-up:", topUpErr);
          }
        }
      }

      if (order.packages?.length > 0) {
        try {
          await AutoFillProgramService.createProgramFromConfirmedOrder(
            order,
            order.packages[0]
          );
        } catch (autoFillErr) {
          console.error("Auto Fill program creation failed:", autoFillErr);
        }
      }
    }
  }

  const usedCashback =
    order.referralSharingBlocked === true ||
    order.appliedDiscounts?.usedCashback === true ||
    (order.appliedDiscounts?.cashbackDiscount || 0) > 0;

  const commissionResults =
    await MultiLevelCommissionService.processOrderCommissions({
      cartOrderId: order.cartOrderId,
      buyerId: order.buyerId,
      buyerName: order.buyerName,
      buyerEmail: order.buyerEmail,
      items: order.items || [],
      packages: order.packages || [],
      totalAmount: order.totalAmount || order.grandTotal,
      orderCounts: order.orderCounts,
      appliedDiscounts: order.appliedDiscounts || {},
      referralSharingBlocked: usedCashback,
    });

  let royaltyResults = null;
  const upgradeOnlyOrder =
    order.packages?.length > 0 &&
    order.packages.every((pkg) => isAutoFillUpgradeOrder(order, pkg));

  if (
    !usedCashback &&
    !upgradeOnlyOrder &&
    order.packages &&
    order.packages.length > 0
  ) {
    for (const pkg of order.packages) {
      if (!orderQualifiesForActivationRoyalty(order, pkg)) continue;

      const orderActivationFee = resolveActivationFeeForRoyalty(order, pkg);

      const { default: RoyaltyDistributionService } = await import(
        "./RoyaltyDistributionService"
      );

      royaltyResults = await RoyaltyDistributionService.distributeRoyalty({
        buyerId: order.buyerId,
        buyerName: order.buyerName,
        buyerEmail: order.buyerEmail,
        packageId: pkg.id,
        packageName: pkg.name,
        packageCategory: pkg.category,
        packagePrice: pkg.price,
        activationFee: orderActivationFee,
        orderId: order.cartOrderId,
        appliedDiscounts: order.appliedDiscounts,
        referralSharingBlocked: usedCashback,
        isAutoFillUpgrade: isAutoFillUpgradeOrder(order, pkg),
      });
      break;
    }
  }

  const hasWalletDiscounts =
    order.appliedDiscounts?.usedCashback ||
    order.appliedDiscounts?.usedCommission;
  if (order.appliedDiscounts && (order.hasAppliedDiscounts || hasWalletDiscounts)) {
    if (order.appliedDiscounts.usedCashback) {
      await CartPurchaseHandler.updateCashbackBalance(
        order.buyerId,
        order.appliedDiscounts.cashbackDiscount,
        "deduct"
      );
    }
    if (order.appliedDiscounts.usedCommission) {
      const totalCommissionDeduction =
        order.appliedDiscounts.commissionDiscount +
        order.appliedDiscounts.commissionAdminFee;
      await CartPurchaseHandler.updateCommissionBalance(
        order.buyerId,
        totalCommissionDeduction,
        "deduct"
      );
    }
  }

  const royaltyTransfer = order.cashRoyaltyTransfer || order.cesCoinsTransfer;
  if (royaltyTransfer?.enabled) {
    const transferResult = await applyCashRoyaltyTransferFromOrder(
      order.buyerId,
      royaltyTransfer,
      order.cartOrderId
    );
    if (!transferResult.success) {
      throw new Error(transferResult.error || "Cash Wallet transfer failed");
    }
    if (transferResult.amount > 0) {
      await updateDoc(orderRef, {
        cashRoyaltyTransfer: {
          ...royaltyTransfer,
          status: "completed",
          completedAt: new Date().toISOString(),
        },
        updatedAt: new Date().toISOString(),
      });
    }
  }

  return {
    success: true,
    commissionResults,
    royaltyResults,
    cartOrderId: order.cartOrderId,
  };
}

export async function confirmByCartOrderId(cartOrderId, options) {
  const order = await loadOrderRecord({ cartOrderId });
  if (!order) {
    return { success: false, error: "Order not found" };
  }
  return confirmCartOrder(order, options);
}

export async function confirmByFirestoreDocId(firestoreDocId, options) {
  const order = await loadOrderRecord({ firestoreDocId });
  if (!order) {
    return { success: false, error: "Order not found" };
  }
  return confirmCartOrder(order, options);
}

export default {
  confirmCartOrder,
  confirmByCartOrderId,
  confirmByFirestoreDocId,
};
